import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../api/client";

interface UseEmployeePhotoResult {
  photoUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

// Simple cache to avoid duplicate requests for the same employee
const photoCache = new Map<number, string | null>();

export const useEmployeePhoto = (employeeId: number): UseEmployeePhotoResult => {
  const {
    data: photoUrl = null,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["employee-photo", employeeId],
    queryFn: async (): Promise<string | null> => {
      // Check cache first
      const cached = photoCache.get(employeeId);
      if (cached !== undefined) {
        return cached;
      }

      try {
        const response = await apiClient.educators.downloadEmployeePhoto(employeeId, false);

        if (response.data) {
          const url = URL.createObjectURL(response.data);
          photoCache.set(employeeId, url);
          return url;
        }

        photoCache.set(employeeId, null);
        return null;
      } catch (err: any) {
        // If the photo is not found (404), that's expected and not an error we need to show
        if (err?.status === 404) {
          photoCache.set(employeeId, null);
          return null;
        }
        // Re-throw other errors so React Query can handle them
        throw new Error(err?.message || "Failed to load photo");
      }
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry for 404 errors
      if (error?.status === 404) return false;
      return failureCount < 2;
    },
  });

  return {
    photoUrl,
    isLoading,
    error: error?.message || null,
  };
};
